/**
 * @fileoverview AR-924 — Port (hexagonal) for the teacher's writes on a student's deliverable.
 * Single-student mutations resolve with the refreshed drawer payload (same shape as GET task_track/deliverable);
 * the group upload (AR-932) resolves with a per-student summary instead.
 */

import type { TaskTrackDeliverable } from '../types'

export interface UploadProgress {
  readonly loaded: number
  readonly total: number
}

export interface UploadFilesArgs {
  readonly activityId: string
  readonly studentId: string
  readonly files: readonly File[]
  readonly onProgress?: (progress: UploadProgress) => void
  /** Aborting rejects with `TeacherDeliverableError('cancelled')`; nothing is kept server-side. */
  readonly signal?: AbortSignal
}

/** Group work: the same files for several students. The backend stores each file once and shares the blob. */
export interface UploadFilesForStudentsArgs {
  readonly activityId: string
  readonly studentIds: readonly string[]
  readonly files: readonly File[]
  readonly onProgress?: (progress: UploadProgress) => void
  readonly signal?: AbortSignal
}

/** Response of POST task_track/deliverable/files/bulk. */
export interface BulkUploadResult {
  readonly uploadedStudentIds: readonly string[]
  /** Students who already submitted themselves are left out, not an error. */
  readonly skipped: ReadonlyArray<{ readonly studentId: string; readonly reason: string }>
}

/**
 * Backend 422 bodies, mapped by the HTTP adapter: plain string ("already submitted" → student_submitted,
 * "Project is finished" → project_finished, "… not found" → not_found, "Work completed date" → invalid_date),
 * `{ files: [...] }` → file_rejected, array of messages → invalid_url. `cancelled` is client-side only.
 */
export type TeacherDeliverableErrorKind =
  | 'student_submitted'
  | 'project_finished'
  | 'file_rejected'
  | 'invalid_url'
  | 'invalid_date'
  | 'not_found'
  | 'cancelled'
  | 'unknown'

export class TeacherDeliverableError extends Error {
  readonly kind: TeacherDeliverableErrorKind
  /** Raw backend messages (may be empty). */
  readonly messages: readonly string[]

  constructor(kind: TeacherDeliverableErrorKind, messages: readonly string[] = []) {
    super(messages[0] ?? kind)
    this.name = 'TeacherDeliverableError'
    this.kind = kind
    this.messages = messages
  }
}

export interface TeacherDeliverablePort {
  fetch(activityId: string, studentId: string): Promise<TaskTrackDeliverable>
  uploadFiles(args: UploadFilesArgs): Promise<TaskTrackDeliverable>
  /** One request for the whole group; a student outside the project fails the whole batch with `not_found`. */
  uploadFilesForStudents(args: UploadFilesForStudentsArgs): Promise<BulkUploadResult>
  removeFile(activityId: string, studentId: string, fileId: string): Promise<TaskTrackDeliverable>
  addLink(activityId: string, studentId: string, url: string): Promise<TaskTrackDeliverable>
  removeLink(activityId: string, studentId: string, linkId: string): Promise<TaskTrackDeliverable>
  /**
   * `workCompletedAt` as `YYYY-MM-DD`. The only way to store the date: uploads and links never carry it,
   * the backend never fills it (null = on time) and it fails with `not_found` before the first upload.
   */
  updateWorkCompletedAt(activityId: string, studentId: string, workCompletedAt: string): Promise<TaskTrackDeliverable>
}
